import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import posthog from "posthog-js";
import { useGetRegistrationEvent } from "../api/registrations";
import {
  Container,
  Loader,
  Center,
  Stack,
  MantineProvider,
  createTheme,
} from "@mantine/core";
import { generateColors } from "@mantine/colors-generator";
import LandingPage from "../components/Registrations/LandingPage";
import EventHeader from "../components/Registrations/EventHeader";
import RegistrationForm from "../components/Registrations/Form/RegistrationForm";
import SponsorsSection from "../components/Registrations/SponsorsSection";
import { useTranslationService } from "../hooks/useTranslationService";
import { useTranslation } from "react-i18next";

function EventRegistration() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const inviteTrackedRef = useRef(false);
  const { language, changeLanguage } = useTranslationService();
  const { t } = useTranslation();

  const [showLanding, setShowLanding] = useState(() => {
    return sessionStorage.getItem(`landing_${slug}`) !== "true";
  });

  const { data, isLoading } = useGetRegistrationEvent({
    eventSlug: slug,
    language,
  });

  const customTheme = useMemo(() => {
    const accentColor = data?.registrationSettings?.color;
    if (!accentColor) return undefined;

    return createTheme({
      defaultRadius: "sm",
      colors: { accent: generateColors(accentColor) },
      primaryColor: "accent",
    });
  }, [data?.registrationSettings?.color]);

  // The active language comes from ?lang / localStorage / the browser, so it can
  // point to a language the event no longer offers. Fall back to the event's
  // main language instead of rendering stale translations.
  useEffect(() => {
    const eventData = data?.event;
    if (!eventData?.language) return;

    const eventLanguages = [
      eventData.language,
      ...(eventData.secondaryLanguages || []),
    ];

    if (!eventLanguages.includes(language)) {
      changeLanguage(eventData.language);
    }
  }, [data?.event, language, changeLanguage]);

  useEffect(() => {
    if (inviteToken && data?.event?.id && !inviteTrackedRef.current) {
      inviteTrackedRef.current = true;
      posthog.capture("invite_page_opened", {
        event_id: data.event.id,
        event_name: data.event.name,
        invite_token: inviteToken,
      });
    }
  }, [inviteToken, data?.event?.id, data?.event?.name]);

  useEffect(() => {
    if (!isLoading && (!data || !data.registrationsEnabled)) {
      navigate("/404", { replace: true });
    }
  }, [data, isLoading, navigate]);

  if (isLoading) {
    return (
      <Container size="md" mt="xl">
        <Center style={{ minHeight: "60vh" }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
          </Stack>
        </Center>
      </Container>
    );
  }

  if (!data) return null;

  const {
    registrationStatus,
    registrationSettings,
    event,
    eventDates,
    approvedTickets = 0,
    sponsors = [],
  } = data;

  const { maxCapacity, displaySponsors } = registrationSettings || {};
  const isCapacityFull = maxCapacity && approvedTickets >= maxCapacity;
  const canRegister = registrationStatus === "open" && !isCapacityFull;

  const renderContent = () => {
    if (showLanding) {
      return (
        <LandingPage
          event={event}
          eventDates={eventDates}
          registrationStatus={registrationStatus}
          registrationSettings={registrationSettings}
          approvedTickets={approvedTickets}
          sponsors={sponsors}
          onProceed={() => {
            sessionStorage.setItem(`landing_${slug}`, "true");
            setShowLanding(false);
          }}
        />
      );
    }

    if (canRegister) {
      return (
        <>
          <EventHeader
            event={event}
            eventDates={eventDates}
            showDetails={true}
          />
          <RegistrationForm
            eventId={event.id}
            inviteToken={inviteToken}
            customFields={data.customFields}
            ticketTypes={data.ticketTypes}
            maxQuantity={registrationSettings?.maxQuantity}
            eventLogo={event?.logoImage}
            eventName={event?.name}
            successMessage={t("registration_success_message")}
            additionalInfo={t("registration_additional_info")}
          />
          <SponsorsSection
            sponsors={sponsors}
            displaySponsors={displaySponsors}
          />
        </>
      );
    }

    return (
      <LandingPage
        event={event}
        eventDates={eventDates}
        registrationStatus={registrationStatus}
        registrationSettings={registrationSettings}
        approvedTickets={approvedTickets}
        sponsors={sponsors}
        onProceed={() => {
          sessionStorage.setItem(`landing_${slug}`, "true");
          setShowLanding(false);
        }}
      />
    );
  };

  return (
    <MantineProvider theme={customTheme}>
      <div
        style={{
          backgroundColor: customTheme?.colors?.accent?.[0],
          minHeight: "100vh",
          paddingTop: 24,
          paddingBottom: 24,
        }}
      >
        <Container size="md" mt="xl" mb="xl">
          {renderContent()}
        </Container>
      </div>
    </MantineProvider>
  );
}

export default EventRegistration;
